import type { components } from "../../../api/generated";

import type {
  GeoAudit,
  GeoCandidate,
  GeoExecuteResult,
  GeoRunDetail,
  GeoRunItem,
  GeoRunPage,
  GeoRuntimeStatus,
  GeoTargetItem,
  GeoTargetPage,
} from "../types";

export type GeoRunItemRaw = components["schemas"]["GeoGovernanceRunItem"];
export type GeoRunDetailRaw = components["schemas"]["GeoGovernanceRunDetail"];
export type GeoRunPageRaw = components["schemas"]["GeoGovernanceRunPage"];
export type GeoTargetItemRaw = components["schemas"]["GeoGovernanceTargetItem"];
export type GeoTargetPageRaw = components["schemas"]["GeoGovernanceTargetPage"];
export type GeoCandidateRaw = components["schemas"]["GeoGovernanceCandidateItem"];
export type GeoAuditRaw = components["schemas"]["GeoGovernanceAuditItem"];
export type GeoExecutePayloadRaw = components["schemas"]["GeoGovernanceExecutePayload"];
export type GeoRuntimeStatusRaw = components["schemas"]["GeoGovernanceRuntimeStatus"];

/** 映射 GEO 治理外部服务运行状态。 */
export function mapGeoRuntimeStatus(raw: GeoRuntimeStatusRaw): GeoRuntimeStatus {
  return {
    ready: raw.ready,
    appEnv: raw.app_env,
    llmProvider: raw.llm_provider,
    llmModel: raw.llm_model,
    llmBaseUrl: raw.llm_base_url,
    llmConfigured: raw.llm_configured,
    amapConfigured: raw.amap_configured,
    message: raw.message,
  };
}

/** 映射 GEO 治理批次。 */
export function mapGeoRunItem(raw: GeoRunItemRaw): GeoRunItem {
  return {
    id: raw.id,
    batchName: raw.batch_name,
    targetTypes: raw.target_types,
    status: raw.status,
    dryRun: raw.dry_run,
    totalCount: raw.total_count,
    processedCount: raw.processed_count,
    resolvedCount: raw.resolved_count,
    reviewRequiredCount: raw.review_required_count,
    failedCount: raw.failed_count,
    createdBy: raw.created_by,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

/** 映射 GEO 治理批次详情。 */
export function mapGeoRunDetail(raw: GeoRunDetailRaw): GeoRunDetail {
  return { ...mapGeoRunItem(raw), selection: raw.selection ?? {} };
}

/** 映射 GEO 治理批次分页。 */
export function mapGeoRunPage(raw: GeoRunPageRaw): GeoRunPage {
  return {
    items: (raw.items ?? []).map(mapGeoRunItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射 GEO 治理对象。 */
export function mapGeoTargetItem(raw: GeoTargetItemRaw): GeoTargetItem {
  return {
    id: raw.id,
    runId: raw.run_id,
    targetType: raw.target_type,
    targetTable: raw.target_table,
    targetId: raw.target_id,
    targetName: raw.target_name,
    province: raw.province,
    city: raw.city,
    district: raw.district,
    address: raw.address,
    contextName: raw.context_name,
    orgName: raw.org_name,
    lng: raw.lng,
    lat: raw.lat,
    status: raw.status,
    candidateCount: raw.candidate_count,
    failureReason: raw.failure_reason,
    inputSha256: raw.input_sha256,
    updatedAt: raw.updated_at,
  };
}

/** 映射 GEO 治理对象分页。 */
export function mapGeoTargetPage(raw: GeoTargetPageRaw): GeoTargetPage {
  return {
    items: (raw.items ?? []).map(mapGeoTargetItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射高德候选。 */
export function mapGeoCandidate(raw: GeoCandidateRaw): GeoCandidate {
  return {
    id: raw.id,
    candidateSeq: raw.candidate_seq,
    provider: raw.provider,
    poiId: raw.poi_id,
    candidateName: raw.candidate_name,
    typeName: raw.type_name,
    province: raw.province,
    city: raw.city,
    district: raw.district,
    address: raw.address,
    lng: raw.lng,
    lat: raw.lat,
    coordinateSystem: raw.coordinate_system,
    searchTerm: raw.search_term,
    verificationStage: raw.verification_stage,
    isQualified: raw.is_qualified,
    qualificationReason: raw.qualification_reason,
  };
}

/** 映射治理审计。 */
export function mapGeoAudit(raw: GeoAuditRaw): GeoAudit {
  return {
    id: raw.id,
    targetId: raw.target_id,
    runId: raw.run_id,
    candidateId: raw.candidate_id,
    reviewAction: raw.review_action,
    reviewNote: raw.review_note,
    beforeGeo: raw.before_geo,
    afterGeo: raw.after_geo,
    createdBy: raw.created_by,
    createdAt: raw.created_at,
  };
}

/** 映射分块执行结果。 */
export function mapGeoExecuteResult(raw: GeoExecutePayloadRaw): GeoExecuteResult {
  return { run: mapGeoRunDetail(raw.run), processedNow: raw.processed_now };
}
